import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import TaskSection from './TaskSection';
import LearningSection from './LearningSection';
import ReflectionSection from './ReflectionSection';
import RoutineSection from './RoutineSection';
import Header from './Header';
import QuoteCard from './QuoteCard';

const DailyBoard = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [tasks, setTasks] = useState([]);
  const [learningGoals, setLearningGoals] = useState([]);
  const [reflections, setReflections] = useState({
    gratitude: '',
    learnings: '',
    tomorrow: ''
  });
  const [routines, setRoutines] = useState({
    morning: {
      affirmation: false,
      meditation: false
    },
    day: {
      dayPlan: false
    },
    evening: {
      dayReflection: false,
      dailyLearning: false,
      weekPlanCheck: false,
      tomorrowPlan: false
    }
  });

  useEffect(() => {
    const savedData = localStorage.getItem('dailyBoard');
    if (savedData) {
      const parsed = JSON.parse(savedData);
      setTasks(parsed.tasks || []);
      setLearningGoals(parsed.learningGoals || []);
      setReflections(parsed.reflections || {
        gratitude: '',
        learnings: '',
        tomorrow: ''
      });
      setRoutines(parsed.routines || {
        morning: {
          affirmation: false,
          meditation: false
        },
        day: {
          dayPlan: false
        },
        evening: {
          dayReflection: false,
          dailyLearning: false,
          weekPlanCheck: false,
          tomorrowPlan: false
        }
      });
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('dailyBoard', JSON.stringify({
      tasks,
      learningGoals,
      reflections,
      routines
    }));
  }, [tasks, learningGoals, reflections, routines]);

  const addTask = (task) => {
    setTasks([...tasks, {
      id: Date.now(),
      ...task,
      completed: false
    }]);
  };

  const toggleTask = (id) => {
    setTasks(tasks.map(task =>
      task.id === id ? { ...task, completed: !task.completed } : task
    ));
  };

  const deleteTask = (id) => {
    setTasks(tasks.filter(task => task.id !== id));
  };

  const addLearningGoal = (goal) => {
    setLearningGoals([...learningGoals, {
      id: Date.now(),
      ...goal,
      completed: false
    }]);
  };

  const toggleLearningGoal = (id) => {
    setLearningGoals(learningGoals.map(goal =>
      goal.id === id ? { ...goal, completed: !goal.completed } : goal
    ));
  };

  const deleteLearningGoal = (id) => {
    setLearningGoals(learningGoals.filter(goal => goal.id !== id));
  };

  const updateReflection = (field, value) => {
    setReflections(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const toggleRoutineItem = (section, item) => {
    setRoutines(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [item]: !prev[section][item]
      }
    }));
  };

  const resetRoutines = () => {
    setRoutines({
      morning: {
        affirmation: false,
        meditation: false
      },
      day: {
        dayPlan: false
      },
      evening: {
        dayReflection: false,
        dailyLearning: false,
        weekPlanCheck: false,
        tomorrowPlan: false
      }
    });
  };

  return (
    <div className="min-h-screen p-4 md:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto">
        <Header currentDate={currentDate} />
        
        {/* Mobile: Single column, Tablet & Desktop: Two columns max */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
          {/* Left Column */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="space-y-6"
          >
            <TaskSection
              tasks={tasks}
              onAddTask={addTask}
              onToggleTask={toggleTask}
              onDeleteTask={deleteTask}
            />
            <LearningSection
              learningGoals={learningGoals}
              onAddGoal={addLearningGoal}
              onToggleGoal={toggleLearningGoal}
              onDeleteGoal={deleteLearningGoal}
            />
          </motion.div>

          {/* Right Column */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-6"
          >
            <RoutineSection
              routines={routines}
              onToggleRoutineItem={toggleRoutineItem}
              onResetRoutines={resetRoutines}
            />
            <QuoteCard />
            <ReflectionSection
              reflections={reflections}
              onUpdateReflection={updateReflection}
            />
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default DailyBoard;